import classNames from "classnames";
import * as React from "react";
import { Component } from "react";
import { memoizeOne } from "~/EasyProjectsCommon/memoizeOne";
import { commonClientResx } from "~/resxDist/commonClientResx";
import { CloseReason } from "~/sharedHOC/ClickOutside/ClickOutside";
import { useMounted } from "../hooks/useMounted";
import { icon, Icon, IconNames } from "../icons";
import { inputTheme } from "../inputs";
import { ItemAdditionalOptions, ListDefaultGroupRow, VirtListRowProps } from "../list/BaseList";
import { AdditionalRow, List, ListProps } from "../list/List";
import { SearchField } from "../list/SearchField";
import * as listStyles from "../list/VirtList.less";
import { getListItemHeight } from "../listRows";
import { Popover, PopoverProps } from "../popover";
import { listInPopoverProps } from "./const/listInPopoverProps";
import {
    DefaultDropdownTrigger,
    DefaultDropdownTriggerProps,
    DEFAULT_DROPDOWN_TRIGGER_OPENED_CLASS,
} from "./DefaultDropdownTrigger";
import * as styles from "./InlinePopupSelect.less";

export enum inlinePopupSelectTheme {
    noBorder = "noBorder",
    inline = "inline",
    table_editor = "table_editor",
    bold = "bold",
}

type InlinePopupBaseProps<TItem, TValue = TItem> = Pick<
    PopoverProps,
    | "renderToBody"
    | "width"
    | "onToggle"
    | "isOpen"
    | "disabled"
    | "openInitially"
    | "wrapperClassName"
    | "disableClosing"
    | "showWithoutIndent"
    | "preventJqueryEsc"
    | "matchTriggerWidth"
    | "minWidth"
    | "placement"
    | "strategy"
    | "fallbackPlacements"
    | "renderTrigger"
> &
    Pick<
        ListProps<TItem>,
        | "blockForFullStory"
        | "itemHeight"
        | "isLoading"
        | "noItemsText"
        | "pageSize"
        | "listMaxWidth"
        | "withAutoRowItemHeight"
        | "getItemLabel"
        | "getItemType"
        | "isItemDisabled"
        | "disabledItemTooltip"
        | "listRowComponent"
        | "getItemSelected"
        | "additionalRows"
    > &
    Pick<SelectTriggerProps<TItem>, "showValidation" | "testId" | "disabledReason"> & {
        options: TItem[];
        emptyOption?: TItem;
        value: TValue | null | undefined;

        disableAutoReset?: boolean;
        /** If true, search field (inner or inline) will not be rendered */
        hideSearch?: boolean;
        /** If true, inline search will be rendered. Ignores {@link InlinePopupBaseProps.hideSearch} */
        inlineSearch?: boolean;
        searchInputTheme?: inputTheme | inputTheme[];
        inputMaxLength?: number;
        searchFieldPlaceholder?: string;

        theme?: inlinePopupSelectTheme;
        selectTriggerPlaceholder?: string;

        canCreate?: boolean;
        isCreating?: boolean;

        dropdownTriggerComponent?: React.SFC<SelectTriggerProps<TItem>>;
        getTriggerIcon?: (item: TItem) => IconNames | JSX.Element;
        getPlaceholderIcon?: () => IconNames | JSX.Element;

        // in some cases it's convenient to specify value as some attribute of
        // option. In this case just specify valueSelector prop.
        valueSelector?: (item: TItem) => TValue;
        optionsComparer?: (item: TItem, value: TItem) => boolean;
        onChangeAction: (value: TValue) => void;
        onCreateAction?: (name: string, callback: F) => void;

        filterOption?: (option: TItem, searchText: string) => boolean;
        loadOptions?: () => void;
        /** If true, control triggers {@link loadOptions} each time it's opened */
        reloadOptionsOnOpen?: boolean;
        getItemHeight?: <T = TItem | DefaultGroupType>(item: T, index: number, items: T[]) => number;
    };

interface InlinePopupPrivateProps {
    isVirtualized?: boolean;
}

interface InlineGroupableSelectProps<TItem> {
    groups?: DefaultGroupType[];
    filterOptionByGroup?: (option: TItem, group: DefaultGroupType) => boolean;
    selectedGroup?: DefaultGroupType;
}

export type InlinePopupSelectProps<TItem, TValue = TItem> = InlinePopupBaseProps<TItem, TValue> &
    InlinePopupPrivateProps &
    InlineGroupableSelectProps<TItem>;

interface InlinePopupSelectState {
    searchInputValue: string;
    selectedGroup?: DefaultGroupType;
}

class InlinePopupSelect<TItem, TValue = TItem> extends Component<
    InlinePopupSelectProps<TItem, TValue>,
    InlinePopupSelectState
> {
    listRef: React.RefObject<List<TItem>> = React.createRef();
    groupsListRef: React.RefObject<List<DefaultGroupType>> = React.createRef();
    popupRef: React.RefObject<Popover> = React.createRef();

    constructor(props: InlinePopupSelectProps<TItem, TValue>) {
        super(props);
        this.state = {
            searchInputValue: "",
            selectedGroup: this.props.selectedGroup,
        };

        this.onSelectChange = this.onSelectChange.bind(this);
    }

    getIsGroupsListDisplayed(): boolean {
        return !!this.props.groups && !this.state.selectedGroup;
    }

    loadOptionsOnce = memoizeOne(() => {
        this.props.loadOptions();
    });

    onToggle = (newValue: boolean, data?: { closeReason: CloseReason }) => {
        if (newValue) {
            if (this.props.reloadOptionsOnOpen) {
                this.props.loadOptions();
            } else if (this.props.loadOptions && this.props.isLoading) {
                this.loadOptionsOnce();
            }
        }

        if (!newValue) {
            this.clearFilterTextChange();
        }

        this.props.onToggle?.(newValue, data);

        if (newValue && this.props.selectedGroup !== this.state.selectedGroup) {
            this.setState({ selectedGroup: this.props.selectedGroup });
        }
    };

    getOptionValue(option: TItem) {
        const valueSelector = this.props.valueSelector;

        // @ts-ignore
        let value: TValue = option;
        if (valueSelector && option != null) {
            value = valueSelector(option);
        }

        return value;
    }

    clearFilterTextChange = () => {
        if (!!this.state.searchInputValue) {
            this.onFilterTextChange("");
        }
    };

    onSelectChange(option: TItem) {
        const creatingItem: ItemAdditionalOptions = option;
        if (creatingItem.addRow) {
            this.props.onCreateAction(creatingItem.newValue, () => {
                this.popupRef.current?.closePopup(CloseReason.CLICK_INSIDE);
            });
        } else {
            const newValue: TValue = this.getOptionValue(option);
            if (this.props.onChangeAction) {
                this.props.onChangeAction(newValue);
                this.popupRef.current?.closePopup(CloseReason.CLICK_INSIDE);
            }
        }
    }

    onGroupSelectChange = (group: DefaultGroupType) => {
        this.setState({ selectedGroup: group });
        this.clearFilterTextChange();
    };

    resetSelectedGroup = () => this.onGroupSelectChange(undefined);

    getDefaultDropdownTriggerComponent = memoizeOne((): React.SFC<SelectTriggerProps<TItem>> => {
        return ({ value, placeholder, isDisabled, tabIndex, testId, disabledReason }) => {
            let text = !!value ? this.props.getItemLabel(value) : placeholder || commonClientResx.select_placeholder;

            const dropdownIcon =
                (!!value && this.props.getTriggerIcon?.(value)) ||
                (!value && this.props.getPlaceholderIcon?.()) ||
                null;

            const itemType = !!value && this.props.getItemType ? this.props.getItemType(value) : null;

            return (
                <DefaultDropdownTrigger
                    text={text}
                    theme={this.props.theme}
                    maxWidth={this.props.width}
                    showValidation={this.props.showValidation}
                    isDisabled={isDisabled}
                    leftIcon={dropdownIcon}
                    blockForFullStory={this.props.blockForFullStory}
                    tabIndex={tabIndex}
                    itemType={itemType}
                    testId={testId}
                    disabledReason={disabledReason}
                />
            );
        };
    });

    getSelectedOptionByValue = memoizeOne((options: TItem[], value: TValue) => {
        return (options || []).find(x => this.props.valueSelector(x) === value);
    });

    getSelectedOption() {
        const { options, value, valueSelector } = this.props;

        // @ts-ignore
        let selectedOption: TItem = value;

        if (valueSelector && value != null) {
            selectedOption = this.getSelectedOptionByValue(options, value);
        }

        return selectedOption;
    }

    onFilterTextChange = (filterText: string) => this.setState({ searchInputValue: filterText });

    onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const ref = this.getIsGroupsListDisplayed() ? this.groupsListRef : this.listRef;
        if (ref.current) {
            ref.current.onKeyDown(event);
        }
    };

    addEmptyOption = memoizeOne((options: TItem[], emptyOption?: TItem) => {
        const addOption = (items: TItem[], item: TItem) => [item, ...items];

        return emptyOption ? addOption(options, emptyOption) : options;
    });

    getOptions = () => {
        const { selectedGroup } = this.state;
        const isGroupSelected = !!selectedGroup;
        const options = isGroupSelected
            ? this.props.options.filter(item => this.props.filterOptionByGroup(item, selectedGroup))
            : this.props.options;

        return this.addEmptyOption(options, this.props.emptyOption);
    };

    getGroupRowComponent = () => {
        const selectedOption = this.getSelectedOption();
        const rowComponent: React.FC<VirtListRowProps<DefaultGroupType>> = ({ item, ...props }) => (
            <ListDefaultGroupRow
                {...props}
                selected={selectedOption && this.props.filterOptionByGroup(selectedOption, item)}
                item={item}
                getItemLabel={getDefaultGroupLabel}
            />
        );

        return rowComponent;
    };

    render() {
        const {
            isVirtualized,
            listMaxWidth,
            width,
            minWidth,
            matchTriggerWidth,
            hideSearch,
            isLoading,
            searchFieldPlaceholder,
            noItemsText,
            inlineSearch,
            searchInputTheme = inputTheme.naked_list_search,
            renderToBody,
            groups,
            itemHeight,
            getItemHeight = getListItemHeight,
            pageSize,
            withAutoRowItemHeight,
            getItemSelected,
            isCreating,
            wrapperClassName,
            dropdownTriggerComponent,
            blockForFullStory,
            showWithoutIndent,
            placement,
            strategy,
            fallbackPlacements,
            renderTrigger,
            testId,
        } = this.props;

        const DropdownTrigger = dropdownTriggerComponent ?? this.getDefaultDropdownTriggerComponent();
        const selectedOption = this.getSelectedOption();
        const additionalRows: AdditionalRow<any>[] = this.props.additionalRows || [
            { options: { isAvailable: this.props.canCreate, showOnSearch: true, hideOnExactMatch: true } },
        ];

        const searchInput = (
            <SearchField
                placeholder={searchFieldPlaceholder}
                value={this.state.searchInputValue}
                onFilterTextChange={this.onFilterTextChange}
                onKeyDown={this.onKeyDown}
                theme={searchInputTheme}
                canCreate={this.props.canCreate}
                maxLength={this.props.inputMaxLength}
                blockForFullStory={this.props.blockForFullStory}
            />
        );

        const options = this.getOptions();

        const { selectedGroup, searchInputValue } = this.state;

        const listCommonProps = {
            itemHeight,
            isLoading,
            pageSize,
            isVirtualized,
            listMaxWidth,
            noItemsText,
            withAutoRowItemHeight,
            searchText: searchInputValue,
            ignoreHomeEndNavigation: !hideSearch,
            isCreating,
            tabIndex: hideSearch && !inlineSearch ? 0 : undefined,
            blockForFullStory,
            getItemHeight,
            additionalRows,
        };

        return (
            <Popover
                strategy={strategy}
                placement={placement}
                fallbackPlacements={fallbackPlacements}
                ref={this.popupRef}
                wrapperClassName={classNames(wrapperClassName, Popover.BLOCK_WRAPPER_CLASS)}
                isOpen={this.props.isOpen}
                onToggle={this.onToggle}
                renderToBody={renderToBody}
                width={width}
                minWidth={minWidth}
                matchTriggerWidth={matchTriggerWidth}
                dontCloseOnTriggerClick={inlineSearch ? true : false}
                openInitially={this.props.openInitially || this.props.inlineSearch}
                disabled={this.props.disabled}
                disableClosing={this.props.disableClosing}
                // disable clicks blocking because sometimes item creation is shown in modal and we should allow user
                // to select values for the new item. For example portfolio creation.
                blockClicksOutside={!isCreating}
                openedClass={DEFAULT_DROPDOWN_TRIGGER_OPENED_CLASS}
                enableDropDownBehavior={true}
                showWithoutIndent={showWithoutIndent}
                preventJqueryEsc={this.props.preventJqueryEsc}
                renderTrigger={renderTrigger}
                {...listInPopoverProps}
            >
                {inlineSearch
                    ? searchInput
                    : !renderTrigger && (
                          <DropdownTrigger
                              value={selectedOption}
                              isDisabled={this.props.disabled}
                              showValidation={this.props.showValidation}
                              placeholder={this.props.selectTriggerPlaceholder}
                              maxWidth={width}
                              blockForFullStory={this.props.blockForFullStory}
                              tabIndex={this.props.disabled ? null : 0}
                              testId={testId}
                              disabledReason={this.props.disabledReason}
                          />
                      )}
                <>
                    {!isLoading && !hideSearch && !inlineSearch && searchInput}
                    {this.getIsGroupsListDisplayed() ? (
                        <List<DefaultGroupType>
                            ref={this.groupsListRef}
                            items={groups}
                            onSelectItem={this.onGroupSelectChange}
                            selectedValue={selectedGroup}
                            getItemLabel={getDefaultGroupLabel}
                            listRowComponent={this.getGroupRowComponent()}
                            {...listCommonProps}
                        />
                    ) : (
                        <>
                            {!!selectedGroup && (
                                <div
                                    onClick={this.resetSelectedGroup}
                                    className={classNames(listStyles.row, listStyles.bold)}
                                >
                                    <Icon iconName={icon.chevron_left} className={styles.arrow_icon} />
                                    <span className={listStyles.label}>{getDefaultGroupLabel(selectedGroup)}</span>
                                </div>
                            )}
                            <List<TItem>
                                ref={this.listRef}
                                items={options}
                                onSelectItem={this.onSelectChange}
                                selectedValue={options && options.length ? selectedOption : null}
                                getItemLabel={this.props.getItemLabel}
                                getItemType={this.props.getItemType}
                                isItemDisabled={this.props.isItemDisabled}
                                disabledItemTooltip={this.props.disabledItemTooltip}
                                listRowComponent={this.props.listRowComponent}
                                filterItem={this.props.filterOption}
                                getItemSelected={getItemSelected}
                                {...listCommonProps}
                            />
                        </>
                    )}
                </>
            </Popover>
        );
    }

    componentDidMount() {
        this._selectFirstOptionIfSelectedNotExists();
    }

    componentDidUpdate(prevProps: Readonly<InlinePopupSelectProps<TItem, TValue>>) {
        const isValueChanged = prevProps.value !== this.props.value;
        const isOptionsChanged =
            prevProps.options !== this.props.options || prevProps.emptyOption !== this.props.emptyOption;

        if (isValueChanged || isOptionsChanged) {
            this._selectFirstOptionIfSelectedNotExists();
        }
    }

    private _selectFirstOptionIfSelectedNotExists() {
        if (this.props.disableAutoReset) {
            return;
        }

        const options = this.getOptions();

        if (options && options.length) {
            const option = this.getSelectedOption();
            const comparer = this.props.optionsComparer ?? ((o1: TItem, o2: TItem) => o1 === o2);

            if (!options.find(o => comparer(o, option))) {
                const newValue: TValue = this.getOptionValue(options[0]);

                this.props.onChangeAction(newValue);
            }
        }
    }
}

export type SelectTriggerProps<TItem> = Pick<
    DefaultDropdownTriggerProps,
    "maxWidth" | "showValidation" | "isDisabled" | "blockForFullStory" | "tabIndex" | "testId" | "disabledReason"
> & {
    value: TItem;
    placeholder?: string;
};

export type DefaultGroupType = {
    GroupName: string;
    IsOtherGroup: boolean;
};

export const getDefaultGroupLabel = (group: DefaultGroupType) => group.GroupName;
const CreatableInlinePopupSelect = <TItem, TValue = TItem>(props: InlinePopupCreateableProps<TItem, TValue>) => {
    const [isCreating, setIsCreating] = React.useState(false);
    const { createAction, canCreate, disabled, onChangeAction, valueSelector } = props;

    if (process.env.NODE_ENV !== "production") {
        if (canCreate && !valueSelector) {
            throw new Error("Create in select only supported with valueSelector prop");
        }
    }

    const isMounted = useMounted();

    const onCreateAction = (newText: string, close: F) => {
        setIsCreating(true);

        const callBack = (newValue: TValue) => {
            // select new item
            onChangeAction(newValue);

            if (isMounted.current) {
                setIsCreating(false);
                close();
            }
        };

        const cancelCreating = () => {
            if (isMounted.current) {
                setIsCreating(false);
                close();
            }
        };

        createAction(newText, callBack, cancelCreating);
    };

    return (
        <InlinePopupSelect
            {...props}
            value={props.value}
            disabled={disabled}
            disableClosing={isCreating}
            options={props.options}
            canCreate={canCreate}
            isCreating={isCreating}
            onCreateAction={onCreateAction}
        />
    );
};

// Everything below this comment was put here (the same file) only for preventing of direct usage of base (InlinePopupSelect) component

/*
 * We want to hide internal props from outside using
 */
export interface InlineCreatableProps<TOption, TValue> {
    createAction?: (newItemText: string, callback: (newItem: TValue) => void, cancelCreating?: () => void) => void;
    canCreate?: boolean;
}
type InlinePopupSimpleProps<TItem, TValue = TItem> = Omit<
    InlinePopupBaseProps<TItem, TValue>,
    "canCreate" | "isVirtualized" | "onCreateAction"
>;
type InlineSelectProps<TItem, TValue = TItem> = InlinePopupSimpleProps<TItem, TValue> &
    InlineCreatableProps<TItem, TValue> &
    InlineGroupableSelectProps<TItem>;
type InlinePopupCreateableProps<TItem, TValue = TItem> = InlineSelectProps<TItem, TValue> & InlinePopupPrivateProps;

// Virtual select
export type InlinePopupSelectVirtProps<TItem, TValue = TItem> = InlineSelectProps<TItem, TValue>;
export const InlinePopupSelectVirt = <TItem extends {}, TValue = TItem>(
    props: InlinePopupSelectVirtProps<TItem, TValue>
) => <CreatableInlinePopupSelect {...props} isVirtualized={true} />;

// Plain select
export type InlinePopupSelectPlainProps<TItem, TValue = TItem> = InlinePopupSelectVirtProps<TItem, TValue>;
export type InlinePopupSelectPlainType<TItem, TValue = TItem> = React.FC<InlinePopupSelectPlainProps<TItem, TValue>>;
export const InlinePopupSelectPlain = <TItem extends {}, TValue = TItem>(
    props: InlinePopupSelectPlainProps<TItem, TValue>
) => <CreatableInlinePopupSelect {...props} isVirtualized={false} />;
